import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * SINS — The Forbidden Whisper Popover
 * 
 * Controls the shadowy reveal and conceal of secrets,
 * anchoring desire’s gaze and managing the dance of presence and absence.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover – The dark veil that reveals hidden truths.
 * @property {HTMLElement} trigger – The temptation that summons the veil.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <sins-anchored-popover data-close-on-resize data-hover-triggered>
 *   <button data-ref="trigger" popovertarget="secrets">Whisper</button>
 *   <div data-ref="popover" id="secrets" popover>Hidden desires unveiled.</div>
 * </sins-anchored-popover>
 * ```
 *
 * @property {string[]} requiredRefs - 'popover' and 'trigger'
 * @property {number} [interaction_delay] - Delay before revealing or hiding
 * @property {string} [data-close-on-resize] - Closes veil on window resize
 * @property {string} [data-hover-triggered] - Activates popover on pointer enter/leave
 * @property {number | null} [popoverTrigger] - Timeout id for reveal/hide actions
 */
export class SinsPopover extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;
  #veilTimer = /** @type {number | null} */ (null);

  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;
    trigger.dataset.hoverActive = 'true';
    if (!popover.matches(':popover-open')) {
      this.#veilTimer = setTimeout(() => {
        if (trigger.matches('[data-hover-active]')) popover.showPopover();
      }, this.interaction_delay);
    }
  };

  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;
    delete trigger.dataset.hoverActive;
    if (this.#veilTimer) clearTimeout(this.#veilTimer);
    if (popover.matches(':popover-open')) {
      this.#veilTimer = setTimeout(() => {
        popover.hidePopover();
      }, this.interaction_delay);
    }
  };

  #onPopoverEnter = () => {
    if (this.#veilTimer) clearTimeout(this.#veilTimer);
  };

  #onPopoverLeave = () => {
    const { popover } = this.refs;
    this.#veilTimer = setTimeout(() => {
      popover.hidePopover();
    }, this.interaction_delay);
  };

  /**
   * Recalculates the position of the veil,
   * setting CSS properties to anchor it precisely to the temptation.
   */
  #updatePosition = async () => {
    const { popover, trigger } = this.refs;
    if (!popover || !trigger) return;
    const bounds = trigger.getBoundingClientRect();
    popover.style.setProperty('--anchor-top', `${bounds.top}px`);
    popover.style.setProperty('--anchor-right', `${window.innerWidth - bounds.right}px`);
    popover.style.setProperty('--anchor-bottom', `${window.innerHeight - bounds.bottom}px`);
    popover.style.setProperty('--anchor-left', `${bounds.left}px`);
    popover.style.setProperty('--anchor-height', `${bounds.height}px`);
    popover.style.setProperty('--anchor-width', `${bounds.width}px`);
  };

  /**
   * Closes the veil on window resize, if specified.
   */
  #resizeListener = debounce(() => {
    const { popover } = this.refs;
    if (popover && popover.matches(':popover-open')) {
      popover.hidePopover();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    const { popover, trigger } = this.refs;

    if (this.dataset.closeOnResize) {
      popover.addEventListener('beforetoggle', (event) => {
        const evt = /** @type {ToggleEvent} */ (event);
        window[evt.newState === 'open' ? 'addEventListener' : 'removeEventListener']('resize', this.#resizeListener);
      });
    }

    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter);
      trigger.addEventListener('pointerleave', this.#onTriggerLeave);
      popover.addEventListener('pointerenter', this.#onPopoverEnter);
      popover.addEventListener('pointerleave', this.#onPopoverLeave);
    }

    if (!CSS.supports('position-anchor: --trigger')) {
      popover.addEventListener('beforetoggle', () => {
        this.#updatePosition();
      });
      requestIdleCallback(() => {
        this.#updatePosition();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
  }
}

if (!customElements.get('sins-anchored-popover')) {
  customElements.define('sins-anchored-popover', SinsPopover);
}
